import { extractApiError, type HttpApiClient } from "@vana/core";
import { CloseDayError } from "../../domain/errors/errors";
import {
  type CloseDayParams,
  type DayCloseRepository,
  type DayCloseSummary,
} from "../../domain/repos/day-close-repo";

interface DayCloseResponse {
  data?: {
    day_close_id: string;
    business_date: string;
    taken_count: number;
    delivered_count: number;
    returned_count: number;
  };
}

const toSummary = (d: NonNullable<DayCloseResponse["data"]>): DayCloseSummary => ({
  dayCloseId: d.day_close_id,
  businessDate: d.business_date,
  takenCount: d.taken_count,
  deliveredCount: d.delivered_count,
  returnedCount: d.returned_count,
});

export class DayCloseRepo implements DayCloseRepository {
  constructor(private readonly apiClient: HttpApiClient) {}

  // The request interceptor snake_cases the body (deliveredRequestIds ->
  // delivered_request_ids, businessDate -> business_date, ...).
  async close(params: CloseDayParams): Promise<DayCloseSummary> {
    try {
      const response = await this.apiClient.post<DayCloseResponse>("v1/day-closes", {
        businessDate: params.businessDate,
        deliveredRequestIds: params.deliveredRequestIds,
        returnedRequestIds: params.returnedRequestIds,
        lat: params.lat,
        long: params.long,
      });
      if (!response.data) {
        throw new CloseDayError("Day close failed");
      }
      return toSummary(response.data);
    } catch (error) {
      if (error instanceof CloseDayError) {
        throw error;
      }
      const { message, apiError } = extractApiError(error);
      throw new CloseDayError(message, apiError);
    }
  }

  // Best-effort: a still-open day (404) or any read failure resolves to null so
  // the screen just shows the open state rather than blocking.
  async getClose(ambassadorId: string, businessDate: string): Promise<DayCloseSummary | null> {
    try {
      const query = new URLSearchParams({
        ambassador_id: ambassadorId,
        business_date: businessDate,
      });
      const response = await this.apiClient.get<DayCloseResponse>(
        `v1/day-closes?${query.toString()}`,
      );
      return response.data ? toSummary(response.data) : null;
    } catch {
      return null;
    }
  }
}
